import React, { useState } from 'react';
import { Calendar, Clock, MapPin, User, Phone, Mail, MessageSquare, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingCalendar = () => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    phone: '',
    email: '',
    cleaning_type: '',
    date: '',
    special_message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cleaningTypes = [
    'Residential Cleaning',
    'Commercial Cleaning',
    'Office Cleaning',
    'Carpet Cleaning',
    'Deep Cleaning',
    'Move In/Out Cleaning'
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, cleaning_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/bookings`, formData);
      toast.success('Booking submitted successfully! We\'ll contact you shortly.');
      setFormData({
        name: '',
        location: '',
        phone: '',
        email: '',
        cleaning_type: '',
        date: '',
        special_message: ''
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="booking-calendar-section">
      <div className="container">
        <Card className="booking-card">
          <CardHeader>
            <CardTitle>
              <Calendar className="inline mr-2" size={28} />
              Book Your Cleaning Service
            </CardTitle>
            <p className="booking-subtitle">Schedule your appointment and we'll take care of the rest</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="booking-form">
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="name">
                    <User size={18} />
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="phone">
                    <Phone size={18} />
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="0412 345 678"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="email">
                    <Mail size={18} />
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="location">
                    <MapPin size={18} />
                    Location *
                  </label>
                  <Input
                    id="location"
                    name="location"
                    type="text"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    placeholder="Your address or suburb"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="cleaning_type">
                    <Briefcase size={18} />
                    Service Type *
                  </label>
                  <Select onValueChange={handleSelectChange} value={formData.cleaning_type} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select cleaning service" />
                    </SelectTrigger>
                    <SelectContent>
                      {cleaningTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-field">
                  <label htmlFor="date">
                    <Clock size={18} />
                    Preferred Date *
                  </label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="special_message">
                  <MessageSquare size={18} />
                  Special Requests (Optional)
                </label>
                <Textarea
                  id="special_message"
                  name="special_message"
                  value={formData.special_message}
                  onChange={handleChange}
                  placeholder="Any special requirements or additional information..."
                  rows={4}
                />
              </div>

              <Button type="submit" className="booking-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Book Now'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default BookingCalendar;
